import React, { useState } from 'react';
import { startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { i18n } from 'next-i18next';
import { useRouter } from 'next/router';
import _ from 'lodash';
import { useAuth } from '@lb-classroom/feature-auth';
import CalendarTimeslot from '../Timeslot';
import TimeslotConstants from '../../../../../constants/TimeslotConstants';
import * as FlashActions from '../../../../../actions/flash';
import * as ModalActions from '../../../../../actions/modal';
import appUtils from '../../../../../utils/appUtils';
import { withTranslation } from 'next-i18next';
import { useSecondaryModalStore } from '../../../../../stores';

const msOfOneDay = 86400000;
const msOfHalfAnHour = 1800000;
const TWENTY_FIVE_MINUTES = 1500000;
const TWENTY_MINUTES = 1200000;
const FIFTY_MINUTES = 3000000;

interface Booking {
  id: string | number;
  start_at: number;
  end_at: number;
  tutor_id: string | number;
}

interface Tutor {
  id: string | number;
}

interface SlotStatus {
  timestamp: number;
  status: string;
}

interface FlashActionsType {
  addMessage: (message: string, options: Record<string, unknown>) => void;
}

interface ModalActionsType {
  hide: () => void;
  addMessage: (message: string, options: Record<string, unknown>) => void;
}

interface CalendarTimeslotTableProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  timeZone?: string;
  now?: Date;
  slotStatus?: SlotStatus[];
  slotStatuses?: SlotStatus[];
  duration?: number;
  myBookings?: Booking[];
  tutor?: Tutor;
  inModal?: boolean;
  loadingSlotStatuses?: boolean;
  flashActions?: FlashActionsType;
  modalActions?: ModalActionsType;
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    flashActions: bindActionCreators(
      FlashActions as unknown as Record<
        string,
        (...args: unknown[]) => unknown
      >,
      dispatch,
    ) as unknown as FlashActionsType,
    modalActions: bindActionCreators(
      ModalActions as unknown as Record<
        string,
        (...args: unknown[]) => unknown
      >,
      dispatch,
    ) as unknown as ModalActionsType,
  };
}

const CalendarTimeslotTable: React.FC<CalendarTimeslotTableProps> = (props) => {
  const {
    t,
    flashActions,
    modalActions,
    slotStatuses,
    duration,
    tutor,
    timeZone,
    myBookings,
    now,
    loadingSlotStatuses,
    inModal = false,
  } = props;
  const language = i18n.language;
  const currentLocale = appUtils.getLocaleFromLang(language);
  const { currentUser } = useAuth();
  const router = useRouter();
  const secondaryModalStore = useSecondaryModalStore();
  const [hoveredSlots, setHoveredSlots] = useState<{
    hoverUp: number | null;
    hoverDown: number | null;
  }>({
    hoverUp: null,
    hoverDown: null,
  });
  const handleMouseEnter = async (timestampOfSlot: number) => {
    changeHoveredState(timestampOfSlot);
  };
  const clearAllSlot = () => {
    setHoveredSlots({
      hoverUp: null,
      hoverDown: null,
    });
  };

  const changeHoveredState = (timestampOfSlot: number) => {
    if (+duration! === 0) {
      return setHoveredSlots({
        hoverUp: timestampOfSlot,
        hoverDown: null,
      });
    }
    if (nextSlotAvailable(timestampOfSlot)) {
      setHoveredSlots({
        hoverUp: timestampOfSlot + msOfHalfAnHour,
        hoverDown: timestampOfSlot,
      });
    }
  };

  const handleMouseLeave = () => {
    clearAllSlot();
  };

  const handleSlotClick = (timestampOfSlot: number) => {
    if (!currentUser) {
      if (inModal) modalActions!.hide();
      router.push({
        pathname: '/signup',
        query: { redirectFrom: 'booking' },
      });
    }
    let cost;
    let startTimestamp;
    let endTimestamp;
    if (+duration! === 1) {
      cost = 2;
      if (nextSlotAvailable(timestampOfSlot)) {
        startTimestamp = timestampOfSlot;
        endTimestamp = startTimestamp + FIFTY_MINUTES;
      } else if (formerSlotAvailable(timestampOfSlot)) {
        startTimestamp = timestampOfSlot - msOfHalfAnHour;
        endTimestamp = startTimestamp + FIFTY_MINUTES;
      } else {
        if (inModal) {
          modalActions!.addMessage(
            'Please try again after changing the lesson duration.',
            {
              autoDisappear: true,
              type: 'error',
            },
          );
          return;
        } else {
          flashActions!.addMessage(
            'Please try again after changing the lesson duration.',
            {
              autoDisappear: true,
              type: 'error',
              sticky: true,
            },
          );
          return;
        }
      }
    } else {
      cost = 1;
      startTimestamp = timestampOfSlot;
      endTimestamp = startTimestamp + TWENTY_FIVE_MINUTES;
    }
    const ticketCount = numberOfTicketsAvailable();
    const subTicketCount = numberoOfNoExpirationTicketsAvailable();
    if (ticketCount + subTicketCount < cost) {
      flashActions!.addMessage(
        "Sorry, you don't have enough number of tickets.",
        {
          type: 'error',
          sticky: true,
          autodisappear: true,
        },
      );
    } else {
      secondaryModalStore.show('booking', {
        secondary: true,
        tutor,
        tutorId: tutor!.id,
        startTimestamp,
        endTimestamp,
        duration,
        timeZone,
        myBookings,
        ticketCount,
        subTicketCount,
        type: 'Book',
      });
    }
  };

  const nextSlotAvailable = (timestampOfSlot: number) => {
    const status = getSlotStatus(timestampOfSlot + msOfHalfAnHour);
    if (status?.status === 'on') return true;
    return false;
  };

  const formerSlotAvailable = (timestampOfSlot: number) => {
    const status = getSlotStatus(timestampOfSlot - msOfHalfAnHour);
    if (status?.status === 'on') return true;
    return false;
  };

  const numberOfTicketsAvailable = () => {
    if (currentUser?.student_profile) {
      return currentUser.student_profile.ticket;
    } else {
      return 0;
    }
  };

  const numberoOfNoExpirationTicketsAvailable = () => {
    if (currentUser?.student_profile) {
      return currentUser.student_profile.sub_ticket;
    } else {
      return 0;
    }
  };

  const getSlotStatus = (timestamp: number) => {
    const tutorId = tutor!.id;
    const id = 'slot_status:' + tutorId.toString() + ':' + timestamp.toString();
    const status = _.findLast(slotStatuses, (s) => {
      return s.timestamp === timestamp;
    });
    const myBooking = _.findLast(myBookings, (b) => {
      return (
        b.start_at === timestamp || b.end_at - TWENTY_MINUTES === timestamp
      );
    });
    if (status && myBooking) {
      if (tutorId === myBooking.tutor_id) {
        return { id, tutor_id: tutorId, timestamp, status: 'cancel' };
      } else {
        return { id, timestamp, status: 'booked' };
      }
    }
    const now2 = new Date().getTime();
    if (now2 + msOfHalfAnHour * 2 >= timestamp || !status) {
      return undefined;
    }
    if (status) {
      switch (status.status) {
        case 'off':
          return undefined;
        default:
          return status;
      }
    }
  };
  let timeslotArray;
  if (currentLocale === 'ja') {
    timeslotArray = TimeslotConstants.slotArrayJa.slice(0, 48);
  } else {
    timeslotArray = TimeslotConstants.slotArray.slice(0, 48);
  }
  let timeslots;
  let slotCountToShow = 0;
  const arrayForCheckNeededRows: Record<string, unknown>[][] = new Array(48);
  if (tutor && !loadingSlotStatuses) {
    timeslotArray.map((time, key) => {
      _.map([0, 1, 2, 3], (n) => {
        const cpOfNow = new Date(now!);
        const zonedDate = toZonedTime(cpOfNow, timeZone!);
        const startOfDayZoned = startOfDay(zonedDate);
        const timestampOfStart = startOfDayZoned.getTime();
        const timestampOfSlot =
          timestampOfStart + msOfOneDay * n + msOfHalfAnHour * key;
        const status = getSlotStatus(timestampOfSlot);
        let hoverStatus: string | null = null;
        if (hoveredSlots.hoverUp === timestampOfSlot) {
          if (hoveredSlots.hoverDown) {
            hoverStatus = 'hover-up';
          } else {
            hoverStatus = 'hovered';
          }
        } else if (hoveredSlots.hoverDown === timestampOfSlot) {
          hoverStatus = 'hover-down';
        }
        if (status) {
          slotCountToShow += 1;
        }
        const slotProps = {
          key: timestampOfSlot,
          tutorId: tutor.id,
          tutor,
          timestamp: timestampOfSlot,
          timeZone,
          status,
          duration,
          myBookings,
          hoverStatus,
          onMouseEnter: () => handleMouseEnter(timestampOfSlot),
          onMouseLeave: () => handleMouseLeave(),
          onClickSlot: () => handleSlotClick(timestampOfSlot),
        };
        arrayForCheckNeededRows[key] = arrayForCheckNeededRows[key] || [];
        arrayForCheckNeededRows[key].push(slotProps);
      });
    });
    let start = 0;
    let end = 47;
    const copiedArray = arrayForCheckNeededRows.slice(0);
    for (const a of copiedArray) {
      let count = 0;
      for (const sp of a) {
        if (typeof sp.status !== 'undefined') {
          count++;
        }
      }
      if (count === 0) {
        start++;
      } else {
        break;
      }
    }
    const copiedArray2 = arrayForCheckNeededRows.slice(0);
    const reversedArray = copiedArray2.reverse();
    for (const a of reversedArray) {
      let count = 0;
      for (const sp of a) {
        if (typeof sp.status !== 'undefined') {
          count++;
        }
      }
      if (count === 0) {
        end--;
      } else {
        break;
      }
    }
    const neededRowsArray = _.range(start, end + 1);
    if (slotCountToShow === 0) {
      timeslots = (
        <tr className="mt-8 mb-4">
          <td colSpan={5} height="70" className="text-center h5">
            {t('student.calendar.noAvailabilities')}
          </td>
        </tr>
      );
    } else {
      timeslots = neededRowsArray.map((val) => {
        const upperOrLower = val % 2 === 0 ? 'upper' : 'lower';
        const ts = [0, 1, 2, 3].map((n) => {
          const slotProps = arrayForCheckNeededRows[val][n];
          return (
            <CalendarTimeslot
              key={`timeslot-${val}-${n}`}
              {...(slotProps as Record<string, unknown>)}
            />
          );
        });
        return (
          <tr key={`calendar-timeslot-${val}`}>
            <td className="side">{val % 2 === 0 ? timeslotArray[val] : ''}</td>
            <td className={upperOrLower}>{ts}</td>
          </tr>
        );
      });
    }
  } else {
    timeslots = (
      <tr className="mt-8 mb-4">
        <td colSpan={5} height="70" className="text-center h5">
          Loading..
        </td>
      </tr>
    );
  }
  return (
    <table className="timeslot-table" cellPadding={0}>
      <tbody>{timeslots}</tbody>
    </table>
  );
};

const wrapped = withTranslation('common')(CalendarTimeslotTable);
export default connect(null, mapDispatchToProps)(wrapped);
